'use strict';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Container, Typography } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { Brand } from '@/lenses/uilenses/theme/colors';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated.js';
import { useLazyGetStudyBuilderEstimateLatestQuery } from '@/redux/services/portal/studyBuilder.api.js';
import { ProductFitForm } from './components/ProductFitForm.jsx';
import { QuoteWizard } from './components/QuoteWizard.jsx';
import { getInitialStudyBuilderSelections, getQuoteProduct, isFitComplete } from './studyBuilder.config.js';
import {
	ESTIMATE_LOAD_FAILED_MESSAGE,
	isStudyBuilderNotFoundError,
	mapStudyBuilderEstimatePresentation,
} from './studyBuilder.estimate.js';
import { clearStudyBuilderGuestSession, readStudyBuilderGuestSession, writeStudyBuilderGuestSession } from './studyBuilderGuestSession.js';
import {
	BUILD_A_STUDY_PATH,
	LATEST_ESTIMATE_NOT_FOUND_MESSAGE,
	LATEST_ESTIMATE_UNAVAILABLE_MESSAGE,
	buildPathWithoutViewLatestEstimate,
	buildViewLatestEstimateReturnUrl,
	isViewLatestEstimateQuery,
} from './studyBuilder.viewLatestEstimate.js';

/**
 * @param {{ exitHomePath?: string, enableGuestSessionPersistence?: boolean }} props
 */
export default function StudyBuilderPage({ exitHomePath = '/home', enableGuestSessionPersistence = false }) {
	const history = useHistory();
	const location = useLocation();
	const isAuthed = useIsAuthenticated();
	const [fetchLatestEstimate] = useLazyGetStudyBuilderEstimateLatestQuery();
	const [phase, setPhase] = useState(() =>
		enableGuestSessionPersistence && readStudyBuilderGuestSession()?.phase === 'build' ? 'build' : 'fit'
	);
	const [selections, setSelections] = useState(() => {
		if (enableGuestSessionPersistence) {
			const saved = readStudyBuilderGuestSession()?.selections;
			if (saved && typeof saved === 'object') return { ...getInitialStudyBuilderSelections(), ...saved };
		}
		return getInitialStudyBuilderSelections();
	});
	const [restoredLatestEstimate, setRestoredLatestEstimate] = useState(null);
	const [latestEstimateLoading, setLatestEstimateLoading] = useState(false);
	const [latestEstimateNotFoundMessage, setLatestEstimateNotFoundMessage] = useState(null);
	const [latestEstimateLoadError, setLatestEstimateLoadError] = useState(null);
	const [viewLatestSettled, setViewLatestSettled] = useState(false);
	const quoteProduct = useMemo(() => getQuoteProduct(selections), [selections]);

	const restoreSanitizedRef = useRef(false);
	const viewLatestRestoreRef = useRef(false);

	useEffect(() => {
		if (!isViewLatestEstimateQuery(location.search)) return;
		if (viewLatestRestoreRef.current) return;

		// Prefer GET restore over post-login auto-POST.
		writeStudyBuilderGuestSession({ revealEstimateAfterLogin: false });
		restoreSanitizedRef.current = true;

		if (!isAuthed) {
			viewLatestRestoreRef.current = true;
			setViewLatestSettled(true);
			const returnPath = buildViewLatestEstimateReturnUrl(window.location.origin);
			localStorage.setItem('returnUrl', returnPath);
			history.push(`/login?return_url=${encodeURIComponent(returnPath)}`);
			return;
		}

		viewLatestRestoreRef.current = true;
		setLatestEstimateLoading(true);
		setLatestEstimateNotFoundMessage(null);
		setLatestEstimateLoadError(null);

		const restore = async () => {
			try {
				const latest = await fetchLatestEstimate().unwrap();
				const nextSelections = {
					...getInitialStudyBuilderSelections(),
					...(latest?.selections && typeof latest.selections === 'object' ? latest.selections : {}),
				};
				// The build phase renders nothing without a resolvable product and a complete fit, so
				// stay on the fit form instead of showing an empty page when a stored record cannot
				// drive the wizard (for example a product family that no longer exists).
				if (!getQuoteProduct(nextSelections) || !isFitComplete(nextSelections)) {
					setSelections(nextSelections);
					setLatestEstimateNotFoundMessage(LATEST_ESTIMATE_UNAVAILABLE_MESSAGE);
					history.replace(buildPathWithoutViewLatestEstimate(location.pathname || BUILD_A_STUDY_PATH, location.search));
					return;
				}
				setSelections(nextSelections);
				setRestoredLatestEstimate(latest);
				setPhase('build');
				history.replace(buildPathWithoutViewLatestEstimate(location.pathname || BUILD_A_STUDY_PATH, location.search));
			} catch (error) {
				if (isStudyBuilderNotFoundError(error)) {
					setLatestEstimateNotFoundMessage(LATEST_ESTIMATE_NOT_FOUND_MESSAGE);
				} else {
					setLatestEstimateLoadError(ESTIMATE_LOAD_FAILED_MESSAGE);
				}
				history.replace(buildPathWithoutViewLatestEstimate(location.pathname || BUILD_A_STUDY_PATH, location.search));
			} finally {
				setLatestEstimateLoading(false);
				setViewLatestSettled(true);
			}
		};

		restore();
	}, [fetchLatestEstimate, history, isAuthed, location.pathname, location.search]);

	const suppressQuoteWizardForLatestRestore =
		latestEstimateLoading
		|| (isViewLatestEstimateQuery(location.search) && !viewLatestSettled);
	useEffect(() => {
		if (!enableGuestSessionPersistence || restoreSanitizedRef.current) return;
		restoreSanitizedRef.current = true;
		if (phase === 'build' && (!quoteProduct || !isFitComplete(selections))) {
			clearStudyBuilderGuestSession();
			setPhase('fit');
		}
	}, [enableGuestSessionPersistence, phase, quoteProduct, selections]);

	useEffect(() => {
		if (!enableGuestSessionPersistence) return;
		const id = window.setTimeout(() => {
			writeStudyBuilderGuestSession({
				phase,
				selections,
			});
		}, 400);
		return () => window.clearTimeout(id);
	}, [enableGuestSessionPersistence, phase, selections]);

	const handleTopBack = () => {
		if (phase === 'build') {
			setPhase('fit');
			return;
		}
		history.push(exitHomePath);
	};

	const handleContinueToQuote = () => {
		if (isFitComplete(selections)) setPhase('build');
	};

	const restoredQuoteDraft = restoredLatestEstimate?.quoteDraft ?? null;
	const initialEstimatePresentation = restoredLatestEstimate
		? mapStudyBuilderEstimatePresentation(restoredLatestEstimate)
		: null;

	return (
		<Box sx={{ background: phase === 'fit' ? '#f8fafc' : Brand.metaboliteWhite, minHeight: 'calc(100vh - 60px)' }}>
			{phase === 'build' && (
				<Box sx={{ backgroundColor: Brand.darkModeBlue, color: Brand.pureWhite }}>
					<Container maxWidth="lg" sx={{ py: 2.5 }}>
						<Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 2, fontWeight: 700 }}>
							Study details & quote
						</Typography>
						<Typography variant="h5" component="h1" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: '-0.02em' }}>
							Study builder
						</Typography>
					</Container>
				</Box>
			)}
			{phase === 'build' && (
				<Box
					sx={{
						height: 5,
						background: `linear-gradient(90deg, ${Brand.molecularBlue} 0%, ${Brand.livingPurple} 45%, ${Brand.activatedRed} 100%)`,
					}}
				/>
			)}
			<Container maxWidth={phase === 'fit' ? 'md' : 'lg'} data-testid="study-builder-page" sx={{ py: { xs: 3, sm: 5 } }}>
				<Button
					data-testid="study-builder-back"
					startIcon={<ArrowBack />}
					onClick={handleTopBack}
					sx={{
						mb: 2,
						color: '#64748b',
						fontWeight: 500,
						textTransform: 'none',
						'&:hover': { background: 'rgba(0, 0, 0, 0.04)' },
					}}
				>
					{phase === 'fit' ? 'Back to home' : 'Back to selections'}
				</Button>

				{latestEstimateLoading ? (
					<Box
						data-testid="study-builder-latest-estimate-loading"
						sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}
					>
						<CircularProgress size={22} />
						<Typography variant="body2" color="text.secondary">
							Loading your latest estimate...
						</Typography>
					</Box>
				) : null}
				{latestEstimateNotFoundMessage ? (
					<Alert severity="info" sx={{ mb: 2 }} data-testid="study-builder-latest-estimate-not-found">
						{latestEstimateNotFoundMessage}
					</Alert>
				) : null}
				{latestEstimateLoadError ? (
					<Alert severity="error" sx={{ mb: 2 }} data-testid="study-builder-latest-estimate-load-error">
						{latestEstimateLoadError}
					</Alert>
				) : null}

				{phase === 'fit' && (
					<ProductFitForm value={selections} onChange={setSelections} onContinue={handleContinueToQuote} />
				)}

				{phase === 'build' && quoteProduct && !suppressQuoteWizardForLatestRestore && (
					<QuoteWizard
						key={restoredLatestEstimate ? 'latest-restored' : 'default'}
						fitSelections={selections}
						primaryProduct={quoteProduct}
						enableGuestSessionPersistence={enableGuestSessionPersistence}
						initialQuoteDraft={restoredQuoteDraft ?? readStudyBuilderGuestSession()?.quote ?? null}
						initialEstimatePresentation={initialEstimatePresentation}
						onBack={() => setPhase('fit')}
					/>
				)}
			</Container>
		</Box>
	);
}
